import liff, { Liff } from "@line/liff";

let liffReadyPromise: Promise<Liff> | null = null;

const LIFF_ID_STORAGE_KEY = "liffId";

// The LIFF id is club configuration, not member data. logout()/forceReLogin()
// wipe localStorage and must put it back (like the tenant id): without it the
// next liff.init on this device fails with INVALID_CONFIG until the member
// re-enters through a LINE entry link, and the login page cannot resolve the
// LINE user id for the OTP login.
export const getStoredLiffId = (): string | null => {
  try {
    return localStorage.getItem(LIFF_ID_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredLiffId = (liffId: string): void => {
  try {
    localStorage.setItem(LIFF_ID_STORAGE_KEY, liffId);
  } catch {
    // locked-down webview: nothing to preserve
  }
};

// Deterministic configuration failure (no LIFF id on this device): retrying
// cannot help, and the remedy is to re-enter from the club's LINE menu.
export const isLiffConfigError = (err: unknown): boolean =>
  (err as { code?: unknown } | null)?.code === "INVALID_CONFIG";

const getLiffIdFromUrlString = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url, globalThis.location.origin);
    return parsedUrl.searchParams.get("liffId");
  } catch {
    return null;
  }
};

const getLiffIdFromLiffState = (): string | null => {
  const liffState = new URLSearchParams(globalThis.location.search).get(
    "liff.state",
  );

  if (!liffState) {
    return null;
  }

  return getLiffIdFromUrlString(decodeURIComponent(liffState));
};

// The LIFF this session was opened through. localStorage.liffId outlives the
// session (and survives logout), so it can belong to a different branch's LIFF.
export const ENTRY_LIFF_ID_SESSION_KEY = "entryLiffId";

const resolveLiffId = async (): Promise<string> => {
  const liffId =
    getLiffIdFromLiffState() ?? getLiffIdFromUrlString(globalThis.location.href);
  if (liffId) {
    localStorage.setItem(LIFF_ID_STORAGE_KEY, liffId);
    try {
      sessionStorage.setItem(ENTRY_LIFF_ID_SESSION_KEY, liffId);
    } catch {
      // locked-down webview: falls back to localStorage.liffId
    }
    return liffId;
  }

  const storedLiffId = localStorage.getItem(LIFF_ID_STORAGE_KEY);
  if (storedLiffId) {
    return storedLiffId;
  }

  // No env fallback: the LIFF id always arrives via liff.state on LINE entry
  // links (then cached above). Without either source, init fails and the app
  // shows its failed_init state.
  return '';
};

export const initLiff = async (): Promise<Liff> => {
  liffReadyPromise ??= resolveLiffId()
    .then((liffId) => liff.init({
      liffId,
      withLoginOnExternalBrowser: true,
    }))
    .then(() => liff)
    .catch((error) => {
      // Never cache a rejected init: a transient failure (network blip,
      // missing liff.state on this open) would otherwise fail every later
      // call until the member kills the webview.
      liffReadyPromise = null;
      throw error;
    });
  return liffReadyPromise;
};
